import { m } from "@plannello/i18n/messages";
import type { ContentLanguage, TranslatedTitle, TranslatedTitles } from "@plannello/shared";
import { type QueryClient, queryOptions, useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import { languageName } from "@/components/translation/content";
import { api } from "@/lib/api";
import { useLocale } from "@/lib/locale";
import { useCatalog } from "./CatalogProvider";
import { type DisplayTitle, displayTitle, type TitleContext, type TitleSubject } from "./displayTitle";

/**
 * Item titles in the reader's language.
 *
 * Content is written in one language, the UI is in another: when the item's language differs from the
 * interface language and a translation of its title exists, every surface shows the translation (lists,
 * board, inbox, timeline, breadcrumbs, hover cards, pickers). Without a translation the original shows.
 * There is no switch for it. The item detail adds a per-item "Show original" toggle.
 *
 * Data: one map per UI locale, `GET /translations/titles?locale=` (every readable product, one query,
 * kept current by `translation.updated` events, see live/apply.ts). Entries are only ever for items
 * written in another language than the locale (the server leaves the others out). A stale entry (made
 * from an older version of the item) still shows until the new translation arrives.
 */

export const titlesKey = (locale: ContentLanguage) => ["translations", "titles", locale] as const;

export const translatedTitlesQuery = (locale: ContentLanguage) =>
  queryOptions({
    queryKey: titlesKey(locale),
    queryFn: ({ signal }) => api.get<TranslatedTitles>(`/api/v1/translations/titles?locale=${locale}`, signal),
    staleTime: 60_000,
    refetchOnReconnect: false,
  });

// ------------------------------------------------------------------------- live patching (apply.ts)

const pending = new Map<ContentLanguage, Map<string, TranslatedTitle>>();
let timer: number | undefined;
const FLUSH_MS = 150;

/**
 * Stores one translated title in the cache. A bulk run delivers hundreds of events a minute, so patches are
 * collected and written together: one copy of the map per burst instead of one per event.
 */
export function patchTranslatedTitle(qc: QueryClient, locale: ContentLanguage, itemId: string, e: TranslatedTitle) {
  const batch = pending.get(locale) ?? new Map<string, TranslatedTitle>();
  pending.set(locale, batch);
  batch.set(itemId, e);
  if (timer !== undefined) return;
  timer = window.setTimeout(() => {
    timer = undefined;
    const all = [...pending];
    pending.clear();
    for (const [l, patches] of all) {
      qc.setQueryData<TranslatedTitles>(titlesKey(l), (old) => {
        // Not loaded yet: the first fetch will include these.
        if (!old) return old;
        return { ...old, ...Object.fromEntries(patches) };
      });
    }
  }, FLUSH_MS);
}

// ---------------------------------------------------------------------------- display (pure helper)

/** Tooltip for a translated title: the original and the language it is in. Undefined for originals. */
export function originalHint(d: DisplayTitle): string | undefined {
  if (!d.translated) return undefined;
  return d.sourceLanguage
    ? m.tr_title_original({ language: languageName(d.sourceLanguage), title: d.original })
    : d.original;
}

/** Screen-reader text next to a translated title ("translated from English"); empty for originals. */
export function translatedLabel(d: DisplayTitle): string | undefined {
  if (!d.translated) return undefined;
  return d.sourceLanguage
    ? m.tr_title_translated_from({ language: languageName(d.sourceLanguage) })
    : m.tr_title_translated();
}

/**
 * Props for the element that shows a title in a dense list: the `title` tooltip is the original for a
 * translated title (else `fallback`, normally the full text for truncated rows).
 */
export function titleTooltip(d: DisplayTitle, fallback?: string): string | undefined {
  return originalHint(d) ?? fallback;
}

// ----------------------------------------------------------------------------------- provider, hooks

const NONE: TitleContext = { locale: "en", titles: undefined, languageOf: () => undefined };

const Ctx = createContext<TitleContext | null>(null);
let latest: TitleContext | null = null;

/** Display title for non-React code (picker option lists, drag data): uses the latest mounted provider's data. */
export function currentDisplayTitle(item: TitleSubject): DisplayTitle {
  return displayTitle(item, latest ?? NONE);
}

/** Mounted once in the authenticated layout, inside the CatalogProvider. */
export function TitleTranslationProvider({ children }: { children: ReactNode }) {
  const locale = useLocale();
  const catalog = useCatalog();
  const q = useQuery(translatedTitlesQuery(locale));
  const titles = q.data;
  const value = useMemo<TitleContext>(
    () => ({
      locale,
      titles,
      languageOf: (item) =>
        item.contentLanguage ?? (item.productId ? catalog.productById.get(item.productId)?.contentLanguage : undefined),
    }),
    [locale, titles, catalog],
  );
  latest = value;
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTitleContext(): TitleContext {
  return useContext(Ctx) ?? NONE;
}

/** A function that maps an item to its display title; stable while the cache and the locale are. */
export function useTitles(): (item: TitleSubject) => DisplayTitle {
  const ctx = useTitleContext();
  return useMemo(() => (item) => displayTitle(item, ctx), [ctx]);
}

/** Display title of one item. */
export function useDisplayTitle(item: TitleSubject): DisplayTitle {
  return displayTitle(item, useTitleContext());
}

/** Non-React access (commands, drag data): the title as the reader sees it, from the query cache. */
export function titleFromCache(
  qc: QueryClient,
  locale: ContentLanguage,
  item: TitleSubject,
  languageOf: TitleContext["languageOf"],
): DisplayTitle {
  return displayTitle(item, { locale, titles: qc.getQueryData<TranslatedTitles>(titlesKey(locale)), languageOf });
}

/**
 * Text that client-side free-text filtering matches besides the original title: the translated title the
 * reader sees. `undefined` for items without one.
 */
export function useExtraTitleText(): (item: TitleSubject) => string | undefined {
  const ctx = useTitleContext();
  return useMemo(
    () => (item) => {
      const d = displayTitle(item, ctx);
      return d.translated ? d.text : undefined;
    },
    [ctx],
  );
}

/** Short inline marker for translated titles: sr-only text. Renders nothing for originals. */
export function TranslatedMark({ d }: { d: DisplayTitle }) {
  const label = translatedLabel(d);
  return label ? <span className="sr-only"> ({label})</span> : null;
}
